import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { dublinWallTimeToDate, dublinWallTimeToIso } from "@/lib/dublin-time";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "@tanstack/react-router";
import { createReservation } from "@/lib/reservations.functions";
import { requiresEmailBooking } from "@/lib/booking-policy";
import { listKaraokePackages, type KaraokePackage } from "@/lib/karaoke.functions";
import { useQuery } from "@tanstack/react-query";
import { joinWaitlist } from "@/lib/waitlist.functions";
import { trackEvent } from "@/lib/analytics";
import { bookingIncludesFood, isTimeAfterCutoff, kitchenRulesFor } from "@/lib/kitchen-hours";
import { Beer, Calendar, Mic, Trophy, Users, UtensilsCrossed, Loader2, CheckCircle2, AlertCircle, Clock } from "lucide-react";


export type BookingType =
  | "pool_table"
  | "food_and_drinks"
  | "sports_viewing"
  | "darts"
  | "group_booking"
  | "karaoke";

const TYPES: { id: BookingType; label: string; icon: typeof Trophy; desc: string }[] = [
  { id: "pool_table", label: "Pool Table", icon: Trophy, desc: "60, 90 or 120 min." },
  { id: "food_and_drinks", label: "Food & Drinks Table", icon: UtensilsCrossed, desc: "Standard table for food & drinks." },
  { id: "sports_viewing", label: "Sports Viewing Table", icon: Mic, desc: "Prime view of a screen." },
  { id: "darts", label: "Darts Area", icon: Beer, desc: "Tournament-grade oche." },
  { id: "group_booking", label: "Group Booking", icon: Users, desc: "Up to 10 online; larger groups by email." },
  { id: "karaoke", label: "Karaoke Terrace", icon: Mic, desc: "Sing-along terrace — group packages available." },
];

type Step = 1 | 2 | 3 | 5;

type Outcome = {
  reference: string;
  status: "confirmed" | "pending_approval" | "pending_payment" | "unavailable";
  booking_type: BookingType;
  starts_at: string;
  duration_minutes: number;
  party_size: number;
  guest_name: string;
  guest_email: string;
};

export function formatPackagePrice(p: KaraokePackage): string {
  const euros = (p.price_cents / 100).toFixed(p.price_cents % 100 === 0 ? 0 : 2);
  return p.price_unit === "per_group" ? `€${euros} per group` : `€${euros} pp`;
}

export function ReservationModal({
  open,
  onOpenChange,
  initialBookingType,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  initialBookingType: BookingType;
}) {
  const [step, setStep] = useState<Step>(1);
  const [bookingType, setBookingType] = useState<BookingType>(initialBookingType);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [party, setParty] = useState(2);
  const [duration, setDuration] = useState(90);
  const [area, setArea] = useState("");
  const [notes, setNotes] = useState("");
  const [packageId, setPackageId] = useState<string | null>(null);

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [marketing, setMarketing] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeRules, setAgreeRules] = useState(false);

  const [busy, setBusy] = useState(false);
  const submittingRef = useRef(false);
  const submissionKeyRef = useRef(crypto.randomUUID());
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [waitlisted, setWaitlisted] = useState(false);

  const create = useServerFn(createReservation);
  const packagesFn = useServerFn(listKaraokePackages);
  const { data: karaokePackages = [] } = useQuery<KaraokePackage[]>({
    queryKey: ["karaoke-packages"],
    queryFn: () => packagesFn(),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });
  const selectedPackage = karaokePackages.find((p) => p.id === packageId) ?? null;
  const addToWaitlist = useServerFn(joinWaitlist);

  async function joinList() {
    if (!outcome) return;
    setError(null);
    setBusy(true);
    try {
      await addToWaitlist({
        data: {
          booking_type: outcome.booking_type,
          desired_at: outcome.starts_at,
          party_size: outcome.party_size,
          notes: notes || null,
          guest: {
            full_name: fullName,
            email: email || null,
            phone,
            marketing_consent: marketing,
          },
        },
      });
      setWaitlisted(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not join the waitlist");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (open) {
      setStep(1);
      setBookingType(initialBookingType);
      setDate("");
      setTime("");
      setParty(2);
      setDuration(90);
      setArea("");
      setNotes("");
      setPackageId(null);
      setFullName("");
      setEmail("");
      setPhone("");
      setMarketing(false);
      setAgreeTerms(false);
      setAgreeRules(false);
      setOutcome(null);
      setWaitlisted(false);
      setError(null);
      submittingRef.current = false;
      submissionKeyRef.current = crypto.randomUUID();
    }
  }, [open, initialBookingType]);

  async function submit() {
    if (!agreeTerms || !agreeRules) {
      setError("Please accept the terms and house rules");
      return;
    }
    // Hard guard against double submit (double click / Enter twice): state
    // updates are async, so the disabled button alone is not enough.
    if (submittingRef.current) return;
    submittingRef.current = true;
    setError(null);
    setBusy(true);
    try {
      const starts = dublinWallTimeToIso(date, time);
      const res = await create({
        data: {
          submission_key: submissionKeyRef.current,
          booking_type: bookingType,
          starts_at: starts,
          duration_minutes: duration,
          party_size: party,
          preferred_area: area || null,
          notes:
            [
              selectedPackage
                ? `Karaoke package: ${selectedPackage.name} (${formatPackagePrice(selectedPackage)})`
                : null,
              notes || null,
            ]
              .filter(Boolean)
              .join(" — ") || null,
          guest: {
            full_name: fullName,
            email,
            phone,
            marketing_consent: marketing,
          },
          agreed_terms: true,
          agreed_house_rules: true,
        },
      });
      setOutcome({
        ...res,
        booking_type: bookingType,
        starts_at: starts,
        duration_minutes: duration,
        party_size: party,
        guest_name: fullName,
        guest_email: email,
      });
      trackEvent("reservation_completed", {
        booking_type: bookingType,
        party_size: party,
        duration_minutes: duration,
        status: res.status,
        reference: res.reference,
      });
      setStep(5);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  }


  // Validation helpers
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const phoneValid = /^\+?[0-9\s()-]{7,}$/.test(phone);

  // Today in Dublin timezone
  const nowDublin = new Date().toLocaleDateString("en-IE", {
    timeZone: "Europe/Dublin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const [dd, mm, yyyy] = nowDublin.split("/");
  const todayStrDublin = `${yyyy}-${mm}-${dd}`;

  const dateValid = !!date && date >= todayStrDublin;

  // Same-day bookings need at least 2 hours notice
  const isToday = date === todayStrDublin;
  const now = new Date();
  const selectedDateTime = date && time ? dublinWallTimeToDate(date, time) : null;
  const hoursUntil = selectedDateTime
    ? (selectedDateTime.getTime() - now.getTime()) / 1000 / 60 / 60
    : -1;
  const timeValid = !!time && (!isToday || hoursUntil >= 2);
  const partyValid = party >= 1 && party <= 80;
  const needsEmailBooking = requiresEmailBooking(bookingType, party);

  const step2Valid = dateValid && timeValid && partyValid;
  const step3Valid = fullName.trim().length >= 2 && emailValid && phoneValid && agreeTerms && agreeRules;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-[100dvh] max-h-[100dvh] w-full max-w-full overflow-y-auto rounded-none border-border bg-card p-5 sm:h-auto sm:max-h-[90vh] sm:max-w-2xl sm:rounded-2xl sm:p-6">
        <DialogTitle className="font-display text-xl sm:text-2xl">Reserve at Plunkets</DialogTitle>
        <DialogDescription className="text-xs sm:text-sm">
          {step < 5
            ? `Step ${step} of 3 — ${
                step === 1
                  ? "What are you booking?"
                  : step === 2
                  ? "When and how many?"
                  : "Your details"
              }`
            : "Booking outcome"}
        </DialogDescription>
        {step < 5 && (
          <div className="mt-1 flex gap-1.5" aria-hidden>
            {[1, 2, 3].map((n) => (
              <span
                key={n}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  n <= step ? "bg-primary" : "bg-border"
                }`}
              />
            ))}
          </div>
        )}


        {/* Step 1 */}
        {step === 1 && (
          <div className="grid gap-3 sm:grid-cols-2">
            {TYPES.map((t) => {
              const Icon = t.icon;
              const active = bookingType === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setBookingType(t.id)}
                  className={`rounded-xl border p-4 text-left transition ${
                    active
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`grid h-9 w-9 place-items-center rounded-full border ${active ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="font-display text-base">{t.label}</p>
                      <p className="text-xs text-muted-foreground">{t.desc}</p>
                    </div>
                  </div>
                </button>
              );
            })}
            <div className="sm:col-span-2 flex justify-end">
              <Button onClick={() => setStep(2)}>Continue</Button>
            </div>
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Date</Label>
              <Input type="date" min={todayStrDublin} value={date} onChange={(e) => setDate(e.target.value)} className="h-11" />
              {date && !dateValid && (
                <p className="mt-1 text-xs text-destructive">Pick today or a future date.</p>
              )}
            </div>
            <div>
              <Label>Time</Label>
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="h-11" />
              {date && (
                <SuggestedSlots
                  date={date}
                  isToday={isToday}
                  selected={time}
                  onPick={setTime}
                  bookingType={bookingType}
                />
              )}
              {time && isToday && hoursUntil < 2 && (
                <p className="mt-1 text-xs text-destructive">Same-day bookings need at least 2 hours notice.</p>
              )}
              {time && date && isTimeAfterCutoff(bookingType, date, time) && (
                <p className="mt-1 text-xs text-destructive">
                  Food bookings on {kitchenRulesFor(dublinWallTimeToIso(date, time)).dayLabel} must start by{" "}
                  {kitchenRulesFor(dublinWallTimeToIso(date, time)).cutoffLabel} — kitchen closes at{" "}
                  {kitchenRulesFor(dublinWallTimeToIso(date, time)).kitchenCloseLabel}.
                </p>
              )}
            </div>
            <div>
              <Label>Guests</Label>
              <Input
                type="number"
                inputMode="numeric"
                min={1}
                max={80}
                value={party}
                onChange={(e) => setParty(Number(e.target.value))}
                className="h-11"
              />
              {!partyValid && (
                <p className="mt-1 text-xs text-destructive">Between 1 and 80 guests.</p>
              )}
            </div>
            <div>
              <Label>Duration</Label>
              <div className="mt-1 flex gap-2">
                {(bookingType === "karaoke" ? [120, 150, 180] : [60, 90, 120]).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setDuration(m)}
                    className={`flex-1 rounded-md border px-3 py-2 text-sm ${
                      duration === m
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border text-muted-foreground"
                    }`}
                  >
                    {m} min
                  </button>
                ))}
              </div>
            </div>
            {bookingType === "karaoke" && karaokePackages.length > 0 && (
              <div className="sm:col-span-2">
                <Label>Karaoke package (optional)</Label>
                <p className="mb-2 mt-1 text-xs text-muted-foreground">
                  Food & drinks packages for bigger groups — we confirm the final price with you.
                </p>
                <div className="grid gap-2 sm:grid-cols-3">
                  {karaokePackages.map((p) => {
                    const active = packageId === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => {
                          const next = active ? null : p.id;
                          setPackageId(next);
                          if (next) {
                            setDuration(Math.min(240, Math.max(60, p.duration_minutes)));
                            if (party < p.min_guests) setParty(p.min_guests);
                          }
                        }}
                        className={`rounded-xl border p-3 text-left transition ${
                          active ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
                        }`}
                      >
                        <p className="font-display text-sm">{p.name}</p>
                        <p className="text-xs text-primary">{formatPackagePrice(p)}</p>
                        <p className="mt-1 text-[11px] text-muted-foreground">
                          {p.min_guests}–{p.max_guests} guests · {p.duration_minutes} min
                        </p>
                        {p.includes.length > 0 && (
                          <ul className="mt-1.5 space-y-0.5 text-[11px] text-muted-foreground">
                            {p.includes.slice(0, 3).map((i) => (
                              <li key={i}>· {i}</li>
                            ))}
                          </ul>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div>
              <Label>Preferred area</Label>
              <Input
                placeholder="e.g. near the screens"
                value={area}
                onChange={(e) => setArea(e.target.value)}
              />
            </div>
            <div className="sm:col-span-2">
              <Label>Notes (optional)</Label>
              <Textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Birthday, accessibility needs, etc."
              />
            </div>
            {needsEmailBooking && (
              <div className="sm:col-span-2 rounded-xl border border-primary/40 bg-primary/5 p-4">
                <p className="font-display text-base text-primary">
                  {bookingType === "karaoke"
                    ? "Karaoke Terrace bookings are arranged by email"
                    : "Groups over 10 are arranged by email"}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  So we can hold the right space and sort food, drinks and timings for your group,
                  please email us and our team will come back to you.
                </p>
                <a
                  href={`mailto:bookings@plunkets.ie?subject=${encodeURIComponent(
                    `Booking enquiry — ${party} guests${bookingType === "karaoke" ? " (Karaoke Terrace)" : ""}`,
                  )}&body=${encodeURIComponent(
                    `Hi Plunkets,\n\nI'd like to book for ${party} guests.\nDate: ${date || "(date)"}\nTime: ${time || "(time)"}\nType: ${bookingType.replace(/_/g, " ")}\n\nName:\nPhone:\n\nThanks!`,
                  )}`}
                  className="mt-3 inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
                  onClick={() =>
                    trackEvent("email_booking_click", { booking_type: bookingType, party_size: party })
                  }
                >
                  Email bookings@plunkets.ie
                </a>
              </div>
            )}
            <div className="sm:col-span-2 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
              <Button variant="ghost" onClick={() => setStep(1)} className="w-full sm:w-auto">Back</Button>
              <Button
                onClick={() => setStep(3)}
                disabled={!step2Valid || needsEmailBooking}
                className="w-full sm:w-auto"
              >
                Continue
              </Button>
            </div>

          </div>
        )}

        {/* Step 3 */}
        {step === 3 && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label>Full name</Label>
              <Input value={fullName} onChange={(e) => setFullName(e.target.value)} autoComplete="name" className="h-11" />
              {fullName.length > 0 && fullName.trim().length < 2 && (
                <p className="mt-1 text-xs text-destructive">Enter your full name.</p>
              )}
            </div>
            <div>
              <Label>Mobile number</Label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+353 …"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                className="h-11"
              />
              {phone.length > 0 && !phoneValid && (
                <p className="mt-1 text-xs text-destructive">Use a valid mobile number (e.g. +353 87 123 4567).</p>
              )}
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11"
              />
              {email.length > 0 && !emailValid && (
                <p className="mt-1 text-xs text-destructive">Enter a valid email address.</p>
              )}
            </div>
            <div className="sm:col-span-2 space-y-3 pt-1">
              <label className="flex items-start gap-2 text-sm">
                <Checkbox checked={agreeTerms} onCheckedChange={(v) => setAgreeTerms(!!v)} className="mt-0.5" />
                <span>I agree to the booking terms</span>
              </label>
              <label className="flex items-start gap-2 text-sm">
                <Checkbox checked={agreeRules} onCheckedChange={(v) => setAgreeRules(!!v)} className="mt-0.5" />
                <span>I agree to the house rules</span>
              </label>
              <label className="flex items-start gap-2 text-sm text-muted-foreground">
                <Checkbox checked={marketing} onCheckedChange={(v) => setMarketing(!!v)} className="mt-0.5" />
                <span>Send me occasional offers and event info</span>
              </label>
            </div>
            <div className="sm:col-span-2 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
              <Button variant="ghost" onClick={() => setStep(2)} className="w-full sm:w-auto">Back</Button>
              <Button
                onClick={submit}
                disabled={!step3Valid || busy}
                className="w-full sm:w-auto"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit reservation"}
              </Button>
            </div>

          </div>
        )}



        {/* Step 5 — outcome */}
        {step === 5 && outcome && <OutcomeView outcome={outcome} />}

        {step === 5 && outcome?.status === "unavailable" && !outcome.reference.startsWith("WAIT") && (
          <div className="mt-4 rounded-xl border border-border bg-muted/20 p-4">
            {waitlisted ? (
              <p className="flex items-center gap-2 text-sm text-emerald-400">
                <CheckCircle2 size={16} /> You're on the waitlist — we'll call you if a
                table frees up.
              </p>
            ) : (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-muted-foreground">
                  Want us to call you if this slot opens up?
                </p>
                <Button variant="outline" onClick={joinList} disabled={busy}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Join the waitlist"}
                </Button>
              </div>
            )}
          </div>
        )}

        {error && (
          <p className="mt-3 flex items-center gap-2 text-sm text-destructive">
            <AlertCircle size={14} /> {error}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SuggestedSlots({
  date,
  isToday,
  selected,
  onPick,
  bookingType,
}: {
  date: string;
  isToday: boolean;
  selected: string;
  onPick: (v: string) => void;
  bookingType: BookingType;
}) {
  // Venue hours: 12:00 - 23:30, every 30 min
  const slots: string[] = [];
  for (let h = 12; h <= 23; h++) {
    for (const m of [0, 30]) {
      if (h === 23 && m > 30) break;
      slots.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }

  const now = new Date();
  const minTime = isToday ? new Date(now.getTime() + 2 * 60 * 60 * 1000) : null;
  const foodBooking = bookingIncludesFood(bookingType);

  const available = slots.filter((s) => {
    const dt = dublinWallTimeToDate(date, s);
    if (minTime && dt.getTime() < minTime.getTime()) return false;
    if (foodBooking && isTimeAfterCutoff(bookingType, date, s)) return false;
    return true;
  });

  if (available.length === 0) {
    return (
      <p className="mt-2 text-xs text-muted-foreground">
        {foodBooking
          ? "No more food-booking slots available — kitchen cutoff reached. Try another date."
          : "No more slots available today — pick another date."}
      </p>
    );
  }

  const visible = available.slice(0, 8);

  return (
    <div className="mt-2">
      <p className="mb-1 text-xs text-muted-foreground">Suggested times</p>
      <div className="flex flex-wrap gap-1.5">
        {visible.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onPick(s)}
            className={`rounded-md border px-2.5 py-1 text-xs transition ${
              selected === s
                ? "border-primary bg-primary/10 text-primary"
                : "border-border text-muted-foreground hover:border-primary/50"
            }`}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function OutcomeView({ outcome }: { outcome: Outcome }) {
  const map = {
    confirmed: {
      title: "Your reservation is confirmed.",
      icon: CheckCircle2,
      color: "text-emerald-400",
    },
    pending_approval: {
      title: "Your table request has been received and is being reviewed by the Plunkets team.",
      icon: Clock,
      color: "text-amber-300",
    },
    pending_payment: {
      title: "Your table is being held while we process your deposit.",
      icon: Clock,
      color: "text-amber-300",
    },
    unavailable: {
      title:
        "Unfortunately, this time is no longer available. Please choose another time or join the waitlist.",
      icon: AlertCircle,
      color: "text-destructive",
    },
  }[outcome.status];
  const Icon = map.icon;
  const typeLabel =
    TYPES.find((t) => t.id === outcome.booking_type)?.label ??
    outcome.booking_type.replaceAll("_", " ");
  const start = new Date(outcome.starts_at);
  const dateStr = start.toLocaleDateString("en-IE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "Europe/Dublin",
  });
  const timeStr = start.toLocaleTimeString("en-IE", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Dublin",
  });

  if (outcome.status === "unavailable") {
    return (
      <div className="space-y-2 py-2">
        <div className="flex items-center gap-2 text-amber-300">
          <Clock size={20} />
          <p className="font-display text-lg">
            All tables are booked at {timeStr} on {dateStr}.
          </p>
        </div>
        <p className="text-sm text-muted-foreground">
          You've been added to the waitlist — we'll contact you if a {typeLabel.toLowerCase()} frees up.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 py-2">
      <div className={`flex items-center gap-2 ${map.color}`}>
        <Icon size={20} />
        <p className="font-display text-lg">{map.title}</p>
      </div>


      <div className="rounded-xl border border-border bg-muted/20 p-4">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">
          Booking summary
        </p>
        <dl className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs text-muted-foreground">Type</dt>
            <dd className="font-medium">{typeLabel}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Guests</dt>
            <dd className="font-medium">{outcome.party_size}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Date</dt>
            <dd className="font-medium">{dateStr}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Time</dt>
            <dd className="font-medium">
              {timeStr}
              <span className="text-muted-foreground"> · {outcome.duration_minutes} min</span>
            </dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-xs text-muted-foreground">Name</dt>
            <dd className="font-medium">{outcome.guest_name}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-xs text-muted-foreground">Reference</dt>
            <dd className="font-mono text-foreground">{outcome.reference}</dd>
          </div>
        </dl>
        <p className="mt-3 text-xs text-muted-foreground">
          A confirmation will be sent to {outcome.guest_email}.
        </p>
      </div>

      <div className="flex justify-end">
        <Button asChild>
          <Link to="/booking-confirmed/$reference" params={{ reference: outcome.reference }}>
            View confirmation
          </Link>
        </Button>
      </div>
    </div>
  );
}
